import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import {
    computeAdherence,
    defaultP7FormData,
    type P7ChecklistItem,
    type P7FormData,
} from "./p7Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

export function LauraP7Workspace(props: Props) {
    const { t } = useTranslation();
    return (
        <LauraStageShell<P7FormData>
            audit={props.audit}
            stageId="p7"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP7FormData}
            title={t("laura", "p7Title")}
            description="Quality checklist, mandatory gate, and planning presentation"
            generateLabel={t("laura", "generateQuality")}
        >
            {({ form, setForm }) => {
                const adherence = computeAdherence(form.checklist);
                const issues = form.checklist.filter(
                    (i) => i.status === "flag" || i.status === "fail",
                );

                return (
                    <div className="flex flex-col gap-4">
                        <Alert variant={adherence >= 70 ? "default" : "destructive"}>
                            <AlertTitle>{t("laura", "mandatoryGate")}</AlertTitle>
                            <AlertDescription>
                                Status: {form.gate_status} · Adherence: {adherence}%
                                {adherence < 70 && " (minimum 70% required)"}
                            </AlertDescription>
                        </Alert>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                size="sm"
                                disabled={
                                    adherence < 70 && !form.override_justification
                                }
                                onClick={() =>
                                    setForm((p) => ({
                                        ...p,
                                        gate_status: "approved",
                                    }))
                                }
                            >
                                Approve → E0
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    setForm((p) => ({
                                        ...p,
                                        gate_status: "changes_requested",
                                    }))
                                }
                            >
                                Request changes
                            </Button>
                        </div>

                        <Field>
                            <FieldLabel>Override justification (if &lt;70%)</FieldLabel>
                            <Textarea
                                rows={2}
                                className="text-xs"
                                value={form.override_justification}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        override_justification: e.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <QualityGauge adherence={adherence} />

                        <ChecklistForm
                            items={form.checklist}
                            onChange={(checklist) =>
                                setForm((p) => ({ ...p, checklist }))
                            }
                        />

                        {issues.length > 0 && (
                            <Card className="border-destructive/40">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">
                                        Issues to resolve
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="flex flex-col gap-2">
                                    {issues.map((item) => (
                                        <div key={item.id} className="text-xs">
                                            <p>{item.criterion}</p>
                                            <Field className="mt-1">
                                                <FieldLabel>Owner</FieldLabel>
                                                <input
                                                    className="h-8 w-full rounded-md border px-2 text-xs"
                                                    value={item.owner ?? ""}
                                                    onChange={(e) => {
                                                        const checklist =
                                                            form.checklist.map((c) =>
                                                                c.id === item.id
                                                                    ? {
                                                                          ...c,
                                                                          owner: e.target.value,
                                                                      }
                                                                    : c,
                                                            );
                                                        setForm((p) => ({
                                                            ...p,
                                                            checklist,
                                                        }));
                                                    }}
                                                />
                                            </Field>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {form.slides.length > 0 && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">
                                        Presentation ({form.slides.length} slides)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>#</TableHead>
                                                <TableHead>Title</TableHead>
                                                <TableHead>Visual</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {form.slides.map((s) => (
                                                <TableRow key={s.number}>
                                                    <TableCell>{s.number}</TableCell>
                                                    <TableCell>{s.title}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {s.visual}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                );
            }}
        </LauraStageShell>
    );
}

function QualityGauge({ adherence }: { adherence: number }) {
    return (
        <Card>
            <CardContent className="flex flex-col items-center gap-2 pt-6">
                <div className="relative flex size-24 items-center justify-center">
                    <Progress
                        value={adherence}
                        className="absolute inset-0 h-24 w-24 rotate-[-90deg] rounded-full opacity-20"
                    />
                    <span
                        className={cn(
                            "text-xl font-bold",
                            adherence >= 70
                                ? "text-primary"
                                : "text-destructive",
                        )}
                    >
                        {adherence}%
                    </span>
                </div>
                <p className="text-xs text-muted-foreground">Quality adherence</p>
            </CardContent>
        </Card>
    );
}

function ChecklistForm({
    items,
    onChange,
}: {
    items: P7ChecklistItem[];
    onChange: (items: P7ChecklistItem[]) => void;
}) {
    if (items.length === 0) {
        return (
            <p className="text-xs text-muted-foreground">
                Generate quality review to populate checklist.
            </p>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            {items.map((item, i) => (
                <Card key={item.id}>
                    <CardContent className="flex flex-col gap-2 pt-4 text-xs">
                        <p className="font-medium">{item.category}</p>
                        <p>{item.criterion}</p>
                        <div className="flex gap-1">
                            {(["pass", "flag", "fail"] as const).map((s) => (
                                <Button
                                    key={s}
                                    type="button"
                                    size="sm"
                                    variant={
                                        item.status === s ? "default" : "outline"
                                    }
                                    className={cn(
                                        item.status === s &&
                                            s === "flag" &&
                                            "bg-secondary",
                                        item.status === s &&
                                            s === "fail" &&
                                            "bg-destructive",
                                    )}
                                    onClick={() => {
                                        const next = [...items];
                                        next[i] = { ...item, status: s };
                                        onChange(next);
                                    }}
                                >
                                    {s}
                                </Button>
                            ))}
                            <Badge variant="outline" className="ml-auto">
                                weight {item.weight}
                            </Badge>
                        </div>
                        <Textarea
                            rows={2}
                            placeholder="Notes"
                            value={item.notes}
                            onChange={(e) => {
                                const next = [...items];
                                next[i] = { ...item, notes: e.target.value };
                                onChange(next);
                            }}
                        />
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
